<template>
  <div :id="componentId" class="al-avatar-content" test-id="al-user">
    <i
      v-if="avatarUrl"
      class="al-avatar"
      :style="{
        backgroundImage: 'url(' + avatarUrl + ')'
      }"
      aria-hidden="true"
    />
    <i v-else class="al-avatar al-avatar-initials" :class="userBackground" aria-hidden="true">
      {{ nameInitial }}
    </i>
    <span v-if="type !== 'avatar'" :id="`title-${componentId}`" class="al-user-name"
      >{{ userInfo.name }} {{ userInfo.last_name }}</span
    >
    <template v-if="userInfo.tooltip && componentId">
      <al-tooltip
        :key="`key-${componentId}`"
        :target="`title-${componentId}`"
        :label="`${userInfo.name} ${userInfo.last_name}`"
        placement="top"
      />
    </template>
  </div>
</template>

<script>
import AlTooltip from '../tooltip/AlTooltip.vue';
/**
 * Simple component to show information about an user, which as name, last name, avatar picture
 * or when user doesn't have a picture, this component shows the initials letter with a random
 * background.
 * @displayName al-user
 */
export default {
  name: 'AlUser',
  components: {
    AlTooltip
  },
  props: {
    id: {
      type: String,
      default: null
    },
    /**
     * Javascript object with user data that will be shown, it is needed name, last_name and
     * _id string, and avatar object with null value when user don't have avatar picture or a
     * structure with small, original, and large field with the url string inside.
     * @type Object
     */
    userInfo: {
      type: Object,
      required: true
    },
    /**
     * Prop to defined which information will show the component, there are two options.
     *
     * **avatar:** to show only user picture, used when the only element available is the avatar.
     *
     * **name:** Used to show the name next to avatar picture.
     * @values avatar, name
     */
    type: {
      type: String,
      required: false,
      default: 'avatar'
    },
    /**
     * Prop to select between five different size, which only change the url taken to show avatar image
     * @values XB,B,D,S,XS
     */
    size: {
      type: String,
      required: false,
      default: 'D'
    },
    /**
     * @ignore
     */
    info: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * @ignore
     */
    withTooltip: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    componentId() {
      // eslint-disable-next-line no-underscore-dangle
      const id = this.id || this._uid;
      return `al-user-${id}`;
    },
    avatarUrl() {
      if (!this.userInfo.avatar) {
        return 'https://cdn.playvox.com/static/images/avatar_default_small.png';
      }
      if (this.size === 'XS' || this.size === 'S') {
        return this.userInfo.avatar.small.url;
      }
      if (this.size === 'B' || this.size === 'XB') {
        return this.userInfo.avatar.large.url;
      }
      return this.userInfo.avatar.original.url;
    },
    nameInitial() {
      return this.userInfo.name[0] && this.userInfo.last_name[0]
        ? this.userInfo.name[0] + this.userInfo.last_name[0]
        : '';
    },
    /**
     * Easy algorithm to assign a random color from _id data in the userInfo prop, this select between eight options
     * distributed in pairs of haxadecimal characters.
     */

    userBackground() {
      // eslint-disable-next-line no-underscore-dangle
      const parityId = this.userInfo._id.slice(-1);
      switch (parityId) {
        case '0':
        case '1':
          return 'al-user-background-sky';
        case '2':
        case '3':
          return 'al-user-background-yellow';
        case '4':
        case '5':
          return 'al-user-background-violet';
        case '6':
        case '7':
          return 'al-user-background-blue';
        case '8':
        case '9':
          return 'al-user-background-forest';
        case 'a':
        case 'b':
          return 'al-user-background-pink';
        case 'c':
        case 'd':
          return 'al-user-background-orange';
        default:
          return 'al-user-background-white';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.al-avatar-content {
  align-items: center;
  display: flex;
  text-decoration: none;

  .al-avatar {
    background-repeat: no-repeat;
    background-size: cover;
    border: 2px solid color(white, lighten, 1);
    border-radius: 50%;
    display: inline-block;
    height: 40px;
    min-height: 40px;
    min-width: 40px;
    overflow: hidden;
    width: 40px;

    &.al-avatar-initials {
      align-items: center;
      display: flex;
      font-size: font-size(xs);
      font-style: normal;
      justify-content: center;
      letter-spacing: 1px;
      text-transform: uppercase;

      &.al-user-background-sky {
        background-color: color(sky-blue, lighten, 3);
        color: color(sky-blue, darken, 1);
      }

      &.al-user-background-yellow {
        background-color: color(yellow, lighten, 3);
        color: color(yellow, darken, 2);
      }

      &.al-user-background-violet {
        background-color: color(violet, lighten, 3);
        color: color(violet, darken, 1);
      }

      &.al-user-background-blue {
        background-color: color(blue, lighten, 3);
        color: color(blue, darken, 1);
      }

      &.al-user-background-forest {
        background-color: color(forest-green, lighten, 3);
        color: color(forest-green, darken, 1);
      }

      &.al-user-background-pink {
        background-color: color(pink, lighten, 2);
        color: color(pink, darken, 3);
      }

      &.al-user-background-orange {
        background-color: color(orange, lighten, 2);
        color: color(orange, darken, 2);
      }

      &.al-user-background-white {
        background-color: color(black, lighten, 3);
        color: color(white, darken, 2);
      }
    }
  }

  .al-user-name {
    color: color(black);
    font-size: font-size(s);
    font-weight: initial;
    margin-left: spacing(2);
  }

  &:hover .al-user-name {
    color: color(green, darken, 1);
    // text-decoration: underline;
  }
}
</style>
